import { useMemo } from "react";
import { useSdk } from "../../contexts/services/hooks/use-services-context";
import type {
    AssignB2BUserLicenseParams,
    RemoveB2BUserLicenseParams,
} from "../../managers/license/types/license";
import type { LicenseDetail } from "../../managers/license/types/license.api";
import type {
    AffectOrgLicenseToUserResponse,
    ListAllUsersAndLicenceResponse,
    OrganizationResponse,
    RemoveOrgLicenseFromB2BUserResponse,
    UpdateUserAdminRoleResponse,
} from "../../managers/license/types/teamData.api";
import type { UserLicensesDetails } from "../../managers/user/types/user";

export type LicenseAdmin = {
    getFullLicensesDetails: () => Promise<UserLicensesDetails>;
    getLicenseDetailFromId: (licenseId: string) => LicenseDetail;
    getTeamLicenseData: () => Promise<OrganizationResponse>;
    getAllUsersFromTenant: (
        userTeamId: string,
    ) => Promise<ListAllUsersAndLicenceResponse>;
    affectTeamLicenseToUser: (
        orgLicenseId: string,
        user: AssignB2BUserLicenseParams,
    ) => Promise<AffectOrgLicenseToUserResponse>;
    removeTeamLicenseFromUser: (
        user: RemoveB2BUserLicenseParams,
    ) => Promise<RemoveOrgLicenseFromB2BUserResponse>;
    setUserTeamAdmin: (
        userId: string,
        userMail: string,
        setAdmin: boolean,
    ) => Promise<UpdateUserAdminRoleResponse>;
};

/** Licences de l'utilisateur et de son équipe, et leur administration. */
export function useLicenseAdmin(): LicenseAdmin {
    const { licenseManager } = useSdk();

    return useMemo<LicenseAdmin>(
        () => ({
            getFullLicensesDetails: () =>
                licenseManager.getFullLicensesDetails(),
            getLicenseDetailFromId: (...args) =>
                licenseManager.getLicenseDetailFromId(...args),
            getTeamLicenseData: () => licenseManager.getTeamLicenseData(),
            getAllUsersFromTenant: (...args) =>
                licenseManager.getAllUsersFromTenant(...args),
            affectTeamLicenseToUser: (...args) =>
                licenseManager.affectTeamLicenseToUser(...args),
            removeTeamLicenseFromUser: (...args) =>
                licenseManager.removeTeamLicenseFromUser(...args),
            setUserTeamAdmin: (...args) =>
                licenseManager.setUserTeamAdmin(...args),
        }),
        [licenseManager],
    );
}
